import { Animation, Divider } from '@kcd/design-system';
import LoadingDotAnimationData from '@kcd/design-system/dist/assets/animations/loading-dot.json';

import Descriptions from '~/components/Descriptions';
import { useDefaultShippingAddress } from '~/domains/address/queries';
import { useShoppingCartPrice } from '~/domains/cart/queries';
import { CartReceiptMembershipNudge } from '~/domains/plusMembership/components/Nudges';
import { IS_BIG_SALE } from '~/domains/plusMembership/constants';
import { useMembershipRegisteredExpectedAmountNudge } from '~/domains/plusMembership/queries';

const Loading = () => (
  <Animation
    style={{ display: 'inline-block' }}
    loop
    speed={2}
    size={24}
    data={LoadingDotAnimationData}
  />
);

const Price = ({
  value,
  loading,
  prefix,
}: {
  loading: boolean;
  value: number;
  prefix?: string;
}) => (
  <div className="flex items-center justify-end break-keep text-right text-paragraph typo-paragraph100-regular">
    {loading && <Loading />}
    <span>{prefix}</span>
    {value.toLocaleString()}원
  </div>
);

const BoldPrice = ({
  value,
  loading,
  prefix,
}: {
  loading: boolean;
  value: number;
  prefix?: string;
}) => (
  <div className="flex min-h-24 items-center justify-end break-keep text-right typo-heading100">
    {loading && <Loading />}
    <span>{prefix}</span>
    {value.toLocaleString()}원
  </div>
);

const Receipt = ({ isSomeChecked }: { isSomeChecked: boolean }) => {
  const { data } = useShoppingCartPrice('SHOPPING_CART');
  const { data: expectedAmountData } =
    useMembershipRegisteredExpectedAmountNudge();

  const address = useDefaultShippingAddress();
  const isLoading = false;
  const membershipDiscountDescription = data?.discountItems.find(
    (item) => item.discountType === 'MEMBERSHIP_EVENT',
  )?.description;

  return (
    <>
      <div className="px-20 pt-16">
        <Descriptions alignment="right">
          <Descriptions.Item label="주문금액">
            <Price value={data?.paymentAmount || 0} loading={isLoading} />
          </Descriptions.Item>

          {address ? (
            <>
              <Descriptions.Item label="배송비">
                <Price
                  loading={isLoading}
                  value={data?.deliveryFee || 0}
                  prefix={(data?.deliveryFee || 0) > 0 ? '+' : undefined}
                />
              </Descriptions.Item>
              <Descriptions.Item
                label="추가배송비"
                description="(제주/도서산간)"
              >
                <Price
                  value={data?.additionalDeliveryFee || 0}
                  loading={isLoading}
                />
              </Descriptions.Item>
            </>
          ) : (
            <Descriptions.Item label="배송비">
              <span className="text-discovery typo-paragraph100-regular">
                배송지를 등록하면 확인할 수 있어요
              </span>
            </Descriptions.Item>
          )}
          {data?.couponDiscountedPrice && (
            <Descriptions.Item label="쿠폰할인">
              <Price
                value={data?.couponDiscountedPrice}
                prefix="-"
                loading={isLoading}
              />
            </Descriptions.Item>
          )}
          {data?.membershipDiscountedPrice && (
            <Descriptions.Item
              label={
                IS_BIG_SALE ? (
                  membershipDiscountDescription ||
                  '빅세일 스탠다드 멤버십 5% 할인'
                ) : (
                  <div className="flex items-center gap-4">
                    {membershipDiscountDescription || '멤버십 5% 할인'}
                  </div>
                )
              }
            >
              <Price
                value={data?.membershipDiscountedPrice}
                prefix="-"
                loading={isLoading}
              />
            </Descriptions.Item>
          )}
        </Descriptions>
        <Divider size={32} />
        <div className="flex flex-col gap-12">
          <div className="flex items-center justify-between text-paragraph">
            <h3 className="break-keep typo-title100-semibold">결제 예정금액</h3>
            <BoldPrice
              value={data?.discountedTotalAmount || 0}
              loading={isLoading}
            />
          </div>
          <CartReceiptMembershipNudge
            isSomeChecked={isSomeChecked}
            discountedTotalAmount={data?.discountedTotalAmount || 0}
            membershipRegisteredExpectedAmount={expectedAmountData?.nudgeAmount}
          />
        </div>
      </div>
    </>
  );
};

export default Receipt;
